<template>
  <div>
    <p>ユーザーに送るファイルを選択してください。</p>
    <div style="margin-top: 8px">
      <FlatButtonStyle size="medium" variant="primary">
        <label :class="$style.label">
          <IconFile :class="$style.icon" /><input
            type="file"
            :class="$style.input"
            accept="image/*,application/pdf"
            @change="onFileChange"
          />
          ファイルを選択する
        </label>
      </FlatButtonStyle>
    </div>
    <div :class="$style.list" v-if="hasFiles">
      <div
        v-for="inputFile in inputFiles"
        :key="inputFile.id"
        :class="$style.filebox"
      >
        <button
          :class="$style.removeButton"
          @click="removeSelectFile(inputFile.id)"
        >
          ×
        </button>
        <p :class="$style.filebox__name">{{ inputFile.file.name }}</p>
      </div>

      <FlatButton :class="$style.submit" @click="uploadFile">
        ファイルをアップロードする
      </FlatButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nanoid } from 'nanoid'
import IconFile from '@/components/icons/IconFile.vue'
import FlatButtonStyle from '@/components/atoms/FlatButtonStyle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { computed, ref } from 'vue'
import {
  useFor400FoaIssueSignedUploadUrLsMutation,
  useFor400FoaCompleteUploadMutation,
} from './FileUpload400FOA.generated'
import { type RoomID } from '@/types/app'
import axios from 'axios'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  roomId: RoomID
  userName: string
}>()

const emit = defineEmits<{
  (e: 'uploaded'): void
}>()

type InputFile = {
  id: string
  file: File
  dataUrl: string
}

const inputFiles = ref<InputFile[]>([])
const hasFiles = computed(() => inputFiles.value.length > 0)

const isUploading = ref(false)

const alert = useAlert()
const onFileChange = async (e: Event) => {
  const target = e.target as HTMLInputElement
  if (!target.files) {
    return
  }

  const files = Array.from(target.files)

  // input 要素を空にする
  // 空にしないと「ファイル選択 -> ファイル選択 削除 -> ファイル選択」ができなくなる
  // （input がすでに持っているファイルを選択されたときに change イベントを発火しないので）
  target.value = ''

  const hasTooLargeFile = files.some(file => file.size > 100 * 1024 * 1024)
  if (hasTooLargeFile) {
    alert.error('ファイルサイズが大きすぎます')
    return
  }

  const hasUnsupportedFile = files.some(f => {
    // eslint-disable-next-line fp/no-mutating-methods
    const ext = f.name.split('.').pop() ?? ''
    return !['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(ext)
  })
  if (hasUnsupportedFile) {
    alert.error('pdf, jpg, png, gifのみアップロードできます')
    return
  }

  const filePromises = files.map(async file => {
    const dataUrl = await buildDataUrl(file)
    return {
      id: nanoid(),
      file,
      dataUrl,
    }
  })
  const newSelected = await Promise.all(filePromises)

  inputFiles.value = [...inputFiles.value, ...newSelected]

  if (inputFiles.value.length >= 5) {
    inputFiles.value = inputFiles.value.slice(0, 5)
    alert.error('一度にアップロードできる画像は5枚までです')
  }
}

const { mutate: issueSignedUploadUrls } =
  useFor400FoaIssueSignedUploadUrLsMutation({})
const { mutate: completeUpload } = useFor400FoaCompleteUploadMutation({})

const uploadFile = async () => {
  if (!hasFiles.value) {
    return
  }

  try {
    isUploading.value = true

    const result = await issueSignedUploadUrls({
      chatRoomId: props.roomId.toString(),
      files: inputFiles.value.map(selectedFile => ({
        name: selectedFile.file.name,
        size: selectedFile.file.size,
        contentType: selectedFile.file.type,
      })),
    })

    if (!result?.data) {
      return
    }

    const resultItems = result.data.for400FOA__issueSignedUploadURLs.items

    // アップロード用の Signed URL を発行する
    const signedUploadUrls = resultItems.map(item => {
      return item.signedUploadURL
    })

    // Signed URL にアップロードする
    const putRequests = signedUploadUrls.map((signedUploadUrl, index) => {
      const { file } = inputFiles.value[index]

      return axios.put(signedUploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      })
    })

    await Promise.all(putRequests)

    // アップロードしたことを Superfrog に通知して、ファイルを登録する
    await completeUpload({
      chatRoomId: props.roomId.toString(),
      uploadRequestIds: resultItems.map(item => item.uploadRequestId),
      uploadRequests: resultItems.map((item, index) => ({
        id: item.uploadRequestId,
        originalFileName: inputFiles.value[index].file.name,
      })),
    })

    window.alert(
      'アップロードしました\n※まだユーザーにファイルは送信されていません'
    )

    inputFiles.value = []

    emit('uploaded')
  } catch (e) {
    console.error(e)
    window.alert('アップロードに失敗しました')
  } finally {
    isUploading.value = false
  }
}

const removeSelectFile = (id: string) => {
  inputFiles.value = inputFiles.value.filter(inputFile => inputFile.id !== id)
}

function buildDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      resolve(reader.result as string)
    }

    reader.onerror = () => {
      reject(reader.error)
    }

    reader.readAsDataURL(file)
  })
}
</script>

<style lang="scss" module>
.label {
  display: inline-flex;
  gap: 4px;
  cursor: pointer;
}

.selectButton {
  margin-top: 8px;
}

.icon {
  font-size: 20px;
}

.input {
  display: none;
}

.list {
  margin-top: 16px;
}

.filebox {
  display: inline-block;
  position: relative;
  padding: 16px;
  border-radius: $border-radius-v2-base;
  width: 100%;
  background-color: white;

  &:nth-child(n + 2) {
    margin-top: 24px;
  }

  &__name {
    font-weight: bold;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

.removeButton {
  position: absolute;
  top: -10px;
  right: -10px;
  border-radius: 50%;
  background-color: rgba(#000000, 0.3);
  width: 30px;
  height: 30px;
  font-size: 18px;
  color: white;
}

.submit {
  margin-top: 16px;
}
</style>
